import { useState, useRef } from "react";
import { api, PHOTO_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimpleIcon,
  TrashIcon,
  ImageIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DotsSixIcon,
} from "@phosphor-icons/react";

export default function PhotoUploader({ carId, photos = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [reordering, setReordering] = useState(false);
  const dragFromIdx = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleFiles = async (files) => {
    if (!carId) {
      toast.error("Πρώτα αποθηκεύστε το αυτοκίνητο");
      return;
    }
    setUploading(true);
    const next = [...photos];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/cars/${carId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(r.data);
        onChange(next);
      } catch {
        toast.error(`Αποτυχία ανεβάσματος: ${f.name}`);
      }
    }
    setUploading(false);
    toast.success(`${files.length} φωτο ανέβηκαν`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const deletePhoto = async (pid) => {
    try {
      await api.delete(`/cars/${carId}/photos/${pid}`);
      onChange(photos.filter((p) => p.id !== pid));
      toast.success("Διαγράφηκε");
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  const persistOrder = async (ordered) => {
    if (!carId) return;
    setReordering(true);
    try {
      await api.put(`/cars/${carId}/photos/reorder`, { order: ordered.map((p) => p.id) });
      onChange(ordered);
    } catch {
      toast.error("Αποτυχία αλλαγής σειράς");
    } finally {
      setReordering(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= photos.length) return;
    const next = [...photos];
    [next[idx], next[target]] = [next[target], next[idx]];
    persistOrder(next);
  };

  const onDragStart = (idx) => (e) => {
    dragFromIdx.current = idx;
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* noop */ }
  };
  const onDragOverCard = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  };
  const onDragLeaveCard = () => setDragOverIdx(null);
  const onDropCard = (idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIdx(null);
    const from = dragFromIdx.current;
    dragFromIdx.current = null;
    if (from === null || from === idx) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    persistOrder(next);
  };

  return (
    <div className="space-y-3" data-testid="photo-uploader">
      <label
        onDragOver={(e) => {
          // Only treat as file drop if files (not internal reorder)
          if (e.dataTransfer.types?.includes("Files")) {
            e.preventDefault();
            setDragActive(true);
          }
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center p-10 border-2 border-dashed cursor-pointer transition-colors ${
          dragActive ? "border-[#0055FF] bg-[#0055FF]/5" : "border-black/20 bg-[#F9FAFB] hover:border-[#0055FF]"
        }`}
        data-testid="photo-dropzone"
      >
        <UploadSimpleIcon size={42} weight="thin" color="#4B5563" />
        <div className="heading font-bold text-base mt-3">Σύρετε φωτογραφίες εδώ</div>
        <div className="text-xs text-[#4B5563] mt-1">ή κάντε κλικ για επιλογή · JPG / PNG / WEBP</div>
        {!carId && (
          <div className="text-[10px] uppercase tracking-wider text-[#E60000] mt-3">
            Αποθηκεύστε πρώτα το όχημα
          </div>
        )}
        <input
          type="file"
          multiple
          accept="image/*"
          disabled={!carId || uploading}
          onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
          className="hidden"
          data-testid="photo-input"
        />
      </label>

      {uploading && <div className="text-xs font-mono text-[#0055FF]">Ανέβασμα…</div>}

      {photos.length > 0 && (
        <>
          <div className="text-xs text-[#4B5563] flex items-center gap-2">
            <DotsSixIcon size={14} weight="bold" />
            Σύρετε ή χρησιμοποιήστε τα βέλη για να αλλάξετε τη σειρά εμφάνισης
            {reordering && <span className="font-mono text-[#0055FF]">· αποθήκευση...</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photo-grid">
            {photos.map((p, idx) => (
              <div
                key={p.id}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOverCard(idx)}
                onDragLeave={onDragLeaveCard}
                onDrop={onDropCard(idx)}
                onDragEnd={onDragLeaveCard}
                className={`relative border group aspect-[4/3] bg-[#F9FAFB] overflow-hidden cursor-move transition ${
                  dragOverIdx === idx ? "border-[#0055FF] ring-2 ring-[#0055FF]/30" : "border-black/10"
                }`}
                data-testid={`photo-card-${idx}`}
              >
                <img src={PHOTO_URL(p.url)} alt="" className="w-full h-full object-cover pointer-events-none" />
                <div className="absolute top-0 left-0 bg-[#0A0A0A] text-white text-[10px] font-mono px-2 py-0.5">
                  {idx + 1}{idx === 0 ? " · Κύρια" : ""}
                </div>
                <div className="absolute inset-x-0 bottom-0 flex justify-between items-center px-1 py-1 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0 || reordering}
                    className="bg-white/90 hover:bg-white text-[#0A0A0A] disabled:opacity-30 p-1"
                    title="Πιο αριστερά"
                    data-testid={`move-left-${idx}`}
                  >
                    <ArrowLeftIcon size={12} />
                  </button>
                  <DotsSixIcon size={16} weight="bold" className="text-white/80" />
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === photos.length - 1 || reordering}
                    className="bg-white/90 hover:bg-white text-[#0A0A0A] disabled:opacity-30 p-1"
                    title="Πιο δεξιά"
                    data-testid={`move-right-${idx}`}
                  >
                    <ArrowRightIcon size={12} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => deletePhoto(p.id)}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-[#E60000] hover:text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Διαγραφή"
                  data-testid={`delete-photo-${p.id}`}
                >
                  <TrashIcon size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {photos.length === 0 && !uploading && carId && (
        <div className="text-center py-4 text-xs text-[#4B5563]">
          <ImageIcon size={28} className="mx-auto" weight="thin" />
          Καμία φωτογραφία ακόμη
        </div>
      )}
    </div>
  );
}
